import { Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, FormControlLabel, FormHelperText, FormLabel, IconButton, Input, ListItemText, makeStyles, MenuItem, Radio, RadioGroup, Select, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import clsx from "clsx";
import { createRef, forwardRef, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { alertPosition, useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { useCustomerIconList } from "../../store/PointAreaSettingState";
import { ReactComponent as ImageSquare } from '../../images/point/default_icon/square.svg';
import { ReactComponent as ImageSpot } from '../../images/point/default_icon/spot.svg';
import { ReactComponent as ImageHospital } from '../../images/point/default_icon/hospital.svg';
import { ReactComponent as ImageHeliport } from '../../images/point/default_icon/heliport.svg';
import { ReactComponent as ImageSubstation } from '../../images/point/default_icon/substation.svg';
import { DATA_HAS_BEEN_CHANGED_ERROR } from "../../utils/LinksAPI";
import RoleControl from "../RoleControl";
import { SUBJ_POINT_AREA_SETTING_ICON, VERB_CREATE, VERB_DELETE, VERB_UPDATE } from "../../utils/AccessControl";

export const POINT_DEFAULT_ICONS = [
	{ iconId: 1, name: "Square", image: <ImageSquare /> },
	{ iconId: 2, name: "Spot", image: <ImageSpot /> },
	{ iconId: 3, name: "Hospital", image: <ImageHospital /> },
	{ iconId: 4, name: "Heliport", image: <ImageHeliport /> },
	{ iconId: 5, name: "Power plant", image: <ImageSubstation /> },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILE_NUMBER = 10;
const SUPPORT_IMAGE_TYPE = [
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
];

const useStyles = makeStyles({
	rootBox: {
		display: 'flex',
		'& > .MuiBox-root': {
			flexGrow: 1,
		},
		'& > .MuiBox-root:not(:first-child)': {
			marginLeft: '50px',
		},
	},
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	caption: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '4px',
		fontSize: '0.875rem',
		height: '34px',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		'&:nth-child(2)': {
			position: 'sticky',
			left: '34px',
			zIndex: 2,
			// fontWeight: 'bold',
			whiteSpace: 'nowrap',
		},
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+2)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	imageCell: {
		padding: '4px',
		width: '32px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	alignRight: {
		textAlign: 'right',
	},
	svgIcon: {
		fill: 'rgb(75, 75, 75)',
		'& .st0': {
			fill: 'inherit !important',
		}
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		},
		'& .required': {
			color: '#f44336',
		}
	},
	menuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuItemCheckbox: {
		padding: 4,
	},
	menuItemText: {
		marginLeft: 4,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	chip: {
		margin: 2,
	},
	dragImageBox: {
		border: '3px dashed rgba(128, 128, 128, 0.5)',
		borderRadius: '2px',
		'& *': {
			cursor: 'pointer',
		}
	},
	dragImageBoxLabel: {
		fontSize: '75%',
		margin: '-6px 0 2px 14px',
	},
	dragImageBoxActive: {
		borderColor: 'rgba(80, 80, 255, 0.8)',
	},
	dragImageTextBox: {
		width: '128px',
		height: '128px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		padding: '10px',
		fontSize: '15px',
		whiteSpace: 'break-spaces',
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
});

const columns = [
	{ id: 'name', label: 'Name' },
	{ id: 'path', label: 'Image' },
];

const formInputList = [
	{
		code: 'name',
		name: 'Name',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[^'"!@#$%^&*();:{}[\]>? 　]{1,20}$/,
			maxLength: 20,
			unique: true,
			reedit: true,
		},
		default: ""
	},
];

const formDefault = formInputList.reduce((a, v) => {a[v.code] = (v.default || ""); return a}, {});


const CompanyIconListTable = forwardRef(({filterWord, handleEdit, setWaiting, setDisabled}, ref) => {
	const {
		companyIconList,
		registerCustomerIcon,
		updateCustomerIcon,
		deleteCustomerIcon,
		refreshCustomerIconList,
	} = useCustomerIconList();
	const [del, setDel] = useState();
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const handleDelete = (row) => {
		setDel(row);
		setOpen(true);
	}

	const sortedCompanyIconList = useMemo(() => {
		return Array.from(companyIconList).sort((a, b) => a.name.localeCompare(b.name));
	}, [companyIconList]);

	useEffect(() => {
		setDisabled(companyIconList.length >= MAX_FILE_NUMBER);
	}, [companyIconList, setDisabled]);

	const list = useCallback(() => {
		if (filterWord === "") {
			return sortedCompanyIconList;
		}
		return sortedCompanyIconList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					default:
						return row[col.id] && row[col.id].indexOf(filterWord) !== -1;
				}
			});
		});
	}, [sortedCompanyIconList, filterWord]);

	useEffect(() => {
		return () => {
			refreshCustomerIconList();
		}
	}, [refreshCustomerIconList]);

	useImperativeHandle(ref, () => ({
		register: (registerData) => {
			setWaiting(true);
			registerCustomerIcon(registerData)
				.then(() => {
					// console.log("register comp");
				})
				.catch(e => {
					console.error("register error", e);
					showError(`${t('Register failed')}`);
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		update: (updateData, updateImageData) => {
			setWaiting(true);
			updateCustomerIcon(updateData, updateImageData)
				.then(() => {
					// console.log("update comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("update failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerIconList();
					} else {
						console.error("update error", e);
						showError(`${t('update_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		delete: (deleteData) => {
			setWaiting(true);
			deleteCustomerIcon(deleteData)
				.then(() => {
					// console.info("delete comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("delete failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerIconList();
					} else {
						console.error("delete error", e);
						showError(`${t('delete_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return companyIconList.every(v => v[item] !== value);
		},
	}));

	// 確認ダイアログ
	const [open, setOpen] = useState(false);

	const handleDeleteOk = () =>{
		const _data = {
			icon_id: del.iconId,
			update_time: del.update_time,
			update_by_user: del.update_by_user
		};
		setOpen(false);
		ref.current.delete(_data);
	};
  
	const handleClose = () => {
	  setOpen(false);
	};
  
	const handleKeyUp = e => {
	  if (e.keyCode === 13) {
		handleDeleteOk();
	  }
	};

	let detail = '';
	if (del) {
		detail = [
			"\n",
			`${t('Name')}: ${del.name}`
		].join('\n');
	}

	return (
		<>
			{list().map((row) => {
				return (
					<TableRow hover role="checkbox" tabIndex={-1} key={row.iconId}>
						<TableCell key='editBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
							<RoleControl subject={SUBJ_POINT_AREA_SETTING_ICON} verb={VERB_UPDATE} >
								<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
									<Tooltip title="edit" arrow >
										<PencilIcon fontSize="small" />
									</Tooltip>
								</IconButton>
							</RoleControl>
						</TableCell>
						{columns.map((column, i) => {
							const value = row[column.id];
							return (
								<TableCell
									key={column.id}
									align={column.align}
									className={clsx(classes.tableCell, column.id === 'path' && classes.imageCell)}
								>
									{
										column.id === 'path' ?
											row['path'] !== null && <img src={row['path']} style={{width: '32px', height: '32px'}} alt={row['name']} /> :
											column.format && typeof value === 'number' ? column.format(value) : value
									}
								</TableCell>
							);
						})}
						<TableCell key='deleteBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
							<RoleControl subject={SUBJ_POINT_AREA_SETTING_ICON} verb={VERB_DELETE} >
								<IconButton aria-label="delete" size="small" className={classes.smallBtn} onClick={() => handleDelete(row)}>
									<Tooltip title="delete" arrow >
										<DeleteIcon fontSize="small" />
									</Tooltip>
								</IconButton>
							</RoleControl>
						</TableCell>
					</TableRow>
				);
			})}
			<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
				<DialogContent>
					<DialogContentText className={classes.newline}>{t('COMPANY_ICON_DELETE_CONFIRM', {detail})}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">Cancel</Button>
					<Button onClick={handleDeleteOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
		</>
	);
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

function IconList() {
	const childRef = useRef({});
	const [formData, setFormData] = useState({});
	const [open, setOpen] = useState(false);
	const [onTarget, setOnTarget] = useState(false);
	const [preview, setPreview] = useState();
	const [imageChanged, setImageChanged] = useState(false);
	const imageRef = useRef();
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const { showError } = useAlert();

	//TOOD
	const handleDelete = (item, name) => () => {
		setValue(item, getValues(item).filter(value => value !== name), {shouldDirty: true});
	};

	const inputRefs = formInputList.reduce((a, v) => (a[v.code] = createRef()) && a, {});

	const { control, formState: { errors, isDirty, isValid }, getValues, reset, setValue } = useForm({
		mode: 'onChange',
	});

	const userGroupListAll = childRef.current.userGroupListAll;
	const conv = useMemo(() => {
		let usergroups = {}
		if (userGroupListAll) {
			usergroups = userGroupListAll.reduce((previousValue, currentValue) => {
				previousValue[currentValue.user_group_id] = currentValue.user_group_name;
				return previousValue;
			}, {});
		}
		return {
			"usergroups": usergroups,
		};
	}, [userGroupListAll]);

	const classes = useStyles();
	const {t} = useTranslation();

	const handleNew = () => {
		setEditMode(false);
		reset(formDefault);
		setFormData({});
		setPreview();
		setImageChanged(false);
		setOpen(true);
	}

	const handleEdit = (row) => {
		setEditMode(true);
		const _formData = Object.assign({}, row);
		Object.keys(formDefault).forEach(key => _formData[key] === null && (_formData[key] = formDefault[key]));
		reset(_formData);
		setFormData(_formData);
		setPreview();
		setImageChanged(false);
		setOpen(true);
	}

	const handleClose = () => {
		setOpen(false);
	}

	const handleSave = () => {
		const data = Object.keys(inputRefs).reduce((a, v) => {
			if (inputRefs[v].current.value !== "") {
				a[v] = inputRefs[v].current.value;
			}
			return a;
		}, {});

		const promise = new Promise((resolve) => {
			if (imageChanged && imageRef.current) {
				const img = imageRef.current;
				const canvas = document.createElement('canvas');
				canvas.width = 32;
				canvas.height = 32;
				const ctx = canvas.getContext('2d');
				ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, 32, 32);
				canvas.toBlob(blob => {
					resolve(blob);
				}, 'image/png');
			} else {
				resolve();
			}
		});

		promise.then(blob => {
			if (!editMode) {
				if (blob) {
					data.customer_icon_image_blob = blob;
				}
				childRef.current.register(data);
			} else {
				const diff = Object.keys(data).reduce((previousValue, currentValue) => {
					if (formData[currentValue] !== data[currentValue]) {
						if (Array.isArray(formData[currentValue]) && Array.isArray(data[currentValue]) ) {
							previousValue[currentValue] = {
								delete: formData[currentValue].filter(v => !data[currentValue].includes(v)),
								register: data[currentValue].filter(v => !formData[currentValue].includes(v)),
							}
						} else {
							previousValue[currentValue] = data[currentValue];
						}
					}
					return previousValue;
				}, {});

				let _data = null;
				if (Object.keys(diff).length > 0) {
					_data = Object.assign({}, diff, {
						icon_id: formData.iconId,
						update_time: formData.update_time,
						update_by_user: formData.update_by_user
					});
				}

				let imageData;
				if (!imageChanged) {
					//画像変更なし
					imageData = null;
				} else if (formData.path && blob) {
					//画像変更
					imageData = {
						delete: [
							{
								icon_id: formData.iconId,
								update_time: formData.update_time,
								update_by_user: formData.update_by_user
							},
						],
						register: [
							{
								icon_id: formData.iconId,
								customer_icon_image_blob: blob,
								update_time: formData.update_time,
								update_by_user: formData.update_by_user
							},
						]
					};
				} else if (formData.path && !blob) {
					//画像削除
					imageData = {
						delete: [
							{
								icon_id: formData.iconId,
								update_time: formData.update_time,
								update_by_user: formData.update_by_user
							},
						],
					};
				} else if (!formData.path && blob) {
					//画像追加
					imageData = {
						register: [
							{
								icon_id: formData.iconId,
								customer_icon_image_blob: blob,
								update_time: formData.update_time,
								update_by_user: formData.update_by_user
							},
						]
					};
				}
				childRef.current.update(_data, imageData);
			}
		});
		setOpen(false);
	}

	const changePreview = file => {
		if (file) {
			if (file.size > MAX_FILE_SIZE) {
				showError(t('File size is too large'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
				return;
			}
			if (!SUPPORT_IMAGE_TYPE.includes(file.type)) {
				showError(t('Unsupported file type'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
				return;
			}
			const fileReader = new FileReader();
			fileReader.onload = (event) => {
				setImageChanged(true);
				setPreview(event.target.result);
			}
			fileReader.readAsDataURL(file);
		} else {
			setPreview();
		}
	}

	const handleImageChange = event => {
		const files = event.target.files;
		if (files.length >= 1) {
			changePreview(files[0]);
		}
	}

	const handleDragOver = event => {
		event.preventDefault();
		setOnTarget(true);
	}

	const handleDragLeave = event => {
		event.preventDefault();
		setOnTarget(false);
	}

	const handleDrop = event => {
		event.preventDefault();
		setOnTarget(false);
		const files = event.dataTransfer.files;
		if (files.length >= 1) {
			changePreview(files[0]);
		}
	}

	const isDisabled = () => {
		if (!editMode) {
			return !(isDirty && isValid && imageChanged)
		} else {
			if (!imageChanged) {
				return !(isDirty && isValid);
			} else {
				return !isValid;
			}
		}
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box className={classes.rootBox} style={{ padding: '10px 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
						<Box className={classes.caption}>
							<Box>{t('Company Icon [up to {{number}}]', { number: MAX_FILE_NUMBER })}</Box>
							<RoleControl subject={SUBJ_POINT_AREA_SETTING_ICON} verb={VERB_CREATE}>
								<Button variant="outlined" size="small" className={classes.btn} disabled={disabled} onClick={handleNew}>{t('New')}</Button>
							</RoleControl>
						</Box>
						<TableContainer className={classes.container}>
							<Table aria-label="sticky table" size="small" className={clsx(classes.table, classes.stickyTable)}>
								<TableHead>
									<TableRow className={classes.tableRow}>
										<TableCell
											key='edit'
											className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
											padding="none"
										>
										</TableCell>
										{columns.map((column, i) => (
											<TableCell
												key={column.id}
												align={column.align}
												style={{ minWidth: 0 }}
												className={clsx(classes.headerCell, classes.tableCell)}
											>
												{t(column.label)}
											</TableCell>
										))}
										<TableCell
											key='delete'
											className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
											padding="none"
										>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<CompanyIconListTable filterWord={""} ref={childRef} handleEdit={handleEdit} setWaiting={setWaiting} setDisabled={setDisabled} />
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
					<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
						<Box className={classes.caption}>
							<Box>{t('Default Icon')}</Box>
						</Box>
						<TableContainer className={classes.container}>
							<Table aria-label="sticky table" size="small" className={clsx(classes.table, classes.stickyTable)}>
								<TableHead>
									<TableRow className={classes.tableRow}>
										<TableCell
											key='edit'
											style={{ width: "32px" }}
											className={clsx(classes.headerCell, classes.tableCell)}
											padding="none"
										>
										</TableCell>
										{columns.map((column, i) => (
											<TableCell
												key={column.id}
												align={column.align}
												style={{ minWidth: 0 }}
												className={clsx(classes.headerCell, classes.tableCell)}
											>
												{t(column.label)}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{POINT_DEFAULT_ICONS.map((row, i) => (
										<TableRow hover tabIndex={-1} key={i}>
											<TableCell key='editBtn' className={classes.tableCell} align="right">{i + 1}</TableCell>
											<TableCell className={clsx(classes.tableCell)}>{t(row.name)}</TableCell>
											<TableCell className={clsx(classes.tableCell)}>
												<SvgIcon viewBox="0 0 512 512" className={classes.svgIcon} style={{ verticalAlign: "middle" }}>
													{row.image}
												</SvgIcon>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				</Suspense>
			</Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
							<Box style={{ marginLeft: 'auto'}}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={isDisabled()}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
							{formInputList.map(item => (
								item.type === 'text' ?
									<Controller
										key={item.code}
										control={control}
										rules={{
											required: item.rules.required ? t('validate_required') : false,
											pattern: {
												value: item.rules.pattern,
												message: t('validate_pattern')
											},
											maxLength: {
												value: item.rules.maxLength,
												message: t('validate_max_length', { maxLength: item.maxLength }),
											},
											validate: value => {
												if (!item.rules.unique) {
													return true;
												}
												if (formData[item.code] === value) {
													return true;
												}
												return childRef.current.check(item.code, value) || t('already exists')
											}
										}}
										render={({ field: { name, onChange, onBlur, value } }) => (
											<TextField
												className={classes.formInputText}
												label={<div>{t(item.name)}{item.rules.required && <span className="required"> * {t('required')}</span>}</div>}
												name={name}
												id={name}
												variant="outlined"
												size="small"
												InputLabelProps={{
													shrink: true,
												}}
												error={!!errors[name]}
												inputRef={inputRefs[name]}
												// defaultValue={formData[name]}
												helperText={t(errors[name]?.message)}
												onChange={onChange}
												onBlur={onBlur}
												value={value}
												disabled={editMode && item.rules.reedit === false}
											/>
										)}
										name={item.code}
									/>
								: item.type === 'radio' ?
									<FormControl
										key={item.code}
										className={classes.formInputText}
									>
										<FormLabel className={classes.dragImageBoxLabel}>{t(item.name)}</FormLabel>
										<Controller
											key={item.code}
											control={control}
											rules={{
												required: item.rules.required ? t('validate_required') : false,
												pattern: {
													value: item.rules.pattern,
													message: t('validate_pattern')
												},
												maxLength: {
													value: item.rules.maxLength,
													message: t('validate_max_length', { maxLength: item.maxLength }),
												},
												validate: value => {
													if (!item.rules.unique) {
														return true;
													}
													if (formData[item.code] === value) {
														return true;
													}
													return childRef.current.check(item.code, value) || t('already exists')
												}
											}}
											render={({ field: { name, onChange, onBlur, value } }) => (
												<RadioGroup row aria-label={name} name={name} value={value} onChange={onChange} className={classes.radioRoot}>
													<FormControlLabel value="point" control={<Radio color="primary" />} label={t('Point')} />
													<FormControlLabel value="circle" control={<Radio color="primary" />} label={t('Circle')} />
													<FormControlLabel value="polygon" control={<Radio color="primary" />} label={t('Polygon')} />
												</RadioGroup>
											)}
											name={item.code}
										/>
									</FormControl>
								: item.type === 'select' ?
									<FormControl
										key={item.code}
										className={classes.formInputText}
									>
										<FormLabel className={classes.dragImageBoxLabel}>{t(item.name)}</FormLabel>
										<Box style={{ minWidth: '473px' }}>
										<Controller
											name={item.code}
											control={control}
											render={({ field: { name, onChange, onBlur, value } }) => (
												<>
													<Select
														labelId={`${item.code}-mutiple-checkbox-label`}
														id={`${name}-mutiple-checkbox`}
														style={{ width: '100%' }}
														multiple
														value={value}
														inputRef={inputRefs[name]}
														onChange={onChange}
														onBlur={onBlur}
														input={<Input id={`${name}-select-multiple-chip`} />}
														renderValue={(selected) => (
															<div className={classes.chips}>
																{selected.map((value) => (
																	<Chip
																		variant="outlined"
																		size="small"
																		key={value}
																		label={(conv[item.code] && conv[item.code][value]) || value}
																		onMouseDown={(event) => {event.stopPropagation();}}
																		onDelete={handleDelete(name, value)}
																		className={classes.chip}
																	/>
																))}
															</div>
														)}
														MenuProps={MenuProps}
													>
														{childRef.current[item.data.list] && childRef.current[item.data.list].map((v) => (
															<MenuItem dense key={v[item.data.key]} value={v[item.data.key]} className={classes.menuItem}>
																<Checkbox checked={value.indexOf(v[item.data.key]) > -1} className={classes.menuItemCheckbox} />
																<ListItemText primary={v[item.data.name]} className={classes.menuItemText} />
															</MenuItem>
														))}
													</Select>
													<FormHelperText className={classes.alignRight}>{value.length}{item.unit}</FormHelperText>
												</>
											)}
											/>
										</Box>
									</FormControl>
								: <></>
							))}
							<FormControl className={classes.formInputText} style={{ alignItems: "start" }}>
								<FormLabel className={classes.dragImageBoxLabel}>{t('Image')} <span className="required"> * {t('required')}</span></FormLabel>
								<Box
									className={clsx(classes.dragImageBox, onTarget && classes.dragImageBoxActive)}
									onDrop={handleDrop}
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
								>
									<label htmlFor="upload-image" style={{ display: 'flex', flexDirection: 'column' }}>
										{!preview && !formData['path'] ?
											<pre className={classes.dragImageTextBox}>
												{t('DROP_IMAGE_DESCRIBE')}
											</pre> :
											<img src={preview || formData['path']} alt="preview" ref={imageRef} style={{ width: '128px', height: '128px' }} />
										}
										<input
											accept="image/*"
											className={classes.input}
											id="upload-image"
											name="image"
											multiple
											type="file"
											onChange={handleImageChange}
											style={{ display: 'none' }}
										/>
									</label>
								</Box>
							</FormControl>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	)
}

export default IconList;

export function PreLoadIcon() {
	const classes = useStyles();

	return (
		<Box style={{ display: 'none' }}>
			{POINT_DEFAULT_ICONS.map((row, i) => (
				<SvgIcon key={row.name} id={`default_icon_${i + 1}`} viewBox="0 0 512 512" className={classes.svgIcon} style={{ width: '32px', height: '32px' }}>
					{row.image}
				</SvgIcon>
			))}
		</Box>
	)
}
